import { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";

// Define una clave para localStorage
const PWA_INSTALLED_FLAG = "pwaInstalledFlag";

function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  // Nuevo estado para saber si la PWA ya está instalada (basado en localStorage o modo standalone)
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  // Detectar si el usuario está en un dispositivo móvil
  // Nota: Esta validación es para el entorno de navegación actual.
  // Una PWA de escritorio no se considerará "móvil".
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    // --- Lógica para detectar si la PWA es instalable (y no está ya instalada) ---
    const beforeInstallPromptHandler = (e: Event) => {
      // Tipamos el evento más específicamente
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log("Evento 'beforeinstallprompt' disparado!");
      // Si este evento se dispara, significa que NO está instalada AÚN
      setIsPwaInstalled(false);
    };

    window.addEventListener("beforeinstallprompt", beforeInstallPromptHandler);

    // --- Lógica para detectar si la app YA está instalada (usando localStorage o standalone) ---
    const checkInstallationStatus = () => {
      const storedFlag = localStorage.getItem(PWA_INSTALLED_FLAG);
      const isStandaloneMode =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone; // Comprobación para Safari en iOS

      // Consideramos instalada si:
      // 1. Está ejecutándose en modo standalone (si ya la abrieron como app).
      // 2. Tenemos la bandera en localStorage (significa que se instaló antes en el navegador).
      const installed = isStandaloneMode || storedFlag === "true";
      setIsPwaInstalled(installed);
      console.log("¿PWA instalada (estado combinado)?", installed);

      // Si está instalada y no estamos en modo standalone, y tenemos un prompt,
      // el prompt ya no es relevante en esta sesión del navegador.
      if (installed && !isStandaloneMode) {
        setIsInstallable(false); // Ya no es instalable, porque ya lo está.
        setDeferredPrompt(null);
      }
    };

    // Comprobación inicial al montar el componente
    checkInstallationStatus();

    // Escuchar cambios en el modo de visualización (por si cambia dinámicamente, aunque es raro)
    window
      .matchMedia("(display-mode: standalone)")
      .addEventListener("change", checkInstallationStatus);

    // --- Escuchar el evento 'appinstalled' (opcional, para futuras instalaciones) ---
    // Este evento se dispara DESPUÉS de que el usuario acepta la instalación.
    const appInstalledHandler = () => {
      console.log("¡La PWA se instaló exitosamente!");
      localStorage.setItem(PWA_INSTALLED_FLAG, "true"); // Guarda la bandera en localStorage
      setIsPwaInstalled(true); // Actualiza el estado a instalado
      setIsInstallable(false); // Ya no es necesario mostrar el botón de instalación
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", appInstalledHandler);

    // Función de limpieza para remover los event listeners al desmontar el componente
    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        beforeInstallPromptHandler
      );
      window
        .matchMedia("(display-mode: standalone)")
        .removeEventListener("change", checkInstallationStatus);
      window.removeEventListener("appinstalled", appInstalledHandler);
    };
  }, []); // El efecto se ejecuta una sola vez al montar el componente

  // Función que maneja el clic del botón, ya sea para instalar o para abrir
  const handleButtonClick = async () => {
    // Si la PWA ya está "instalada" (según nuestra lógica combinada)
    // Y no estamos en modo standalone (es decir, el usuario está en el navegador),
    // entonces el botón es para "Abrir App".
    if (
      isPwaInstalled &&
      !window.matchMedia("(display-mode: standalone)").matches &&
      !(window.navigator as any).standalone
    ) {
      alert(
        "La aplicación ya está instalada. Ábrela desde tu escritorio o menú de aplicaciones."
      );
      return;
    }

    // Si la PWA no está instalada, pero es instalable (deferredPrompt disponible),
    // muestra el prompt de instalación.
    if (deferredPrompt) {
      try {
        (deferredPrompt as any).prompt(); // Muestra el prompt de instalación al usuario
        const choiceResult = await (deferredPrompt as any).userChoice; // Espera la elección del usuario

        if (choiceResult.outcome === "accepted") {
          console.log("Usuario aceptó instalar la PWA.");
          localStorage.setItem(PWA_INSTALLED_FLAG, "true"); // Guarda la bandera al instalar
          setIsPwaInstalled(true); // Actualiza el estado: la PWA ahora está instalada
        } else {
          console.log("Usuario rechazó la instalación de la PWA.");
        }
      } catch (error) {
        console.error("Error al mostrar el prompt de instalación:", error);
      } finally {
        // Resetea el estado una vez que el prompt ha sido manejado (aceptado o rechazado)
        setDeferredPrompt(null);
        setIsInstallable(false); // No ofrecer la instalación de nuevo en esta sesión
      }
    } else {
      console.log("No hay prompt disponible para instalación.");
    }
  };

  // --- Lógica de renderizado condicional ---

  // 1. Si es un dispositivo móvil, no renderizar nada (según tu requisito de solo escritorio).
  if (isMobile) {
    return null;
  }

  // 2. Si la PWA YA ESTÁ instalada (según localStorage o si ya se está ejecutando en standalone),
  // Y NO estamos en modo standalone (es decir, estamos en el navegador),
  // mostramos el botón para "Abrir App".
  if (
    isPwaInstalled &&
    !window.matchMedia("(display-mode: standalone)").matches &&
    !(window.navigator as any).standalone
  ) {
    return (
      <Button
        onClick={handleButtonClick}
        variant="outlined"
        startIcon={
          <Box
            component="img"
            src="/assets/images/computer.png" // Icono de escritorio
            alt="Icono de aplicación instalada"
            sx={{ width: 32, height: 32 }}
          />
        }
        sx={{
          borderRadius: "12px",
          borderWidth: 2,
          px: { xs: 3, md: 2 },
          py: { xs: 1.5, md: 0.5 },
          color: "#1976d2", // Un color diferente para "Abrir" (ej: azul)
          borderColor: "#1976d2",
          fontWeight: "600",
          fontSize: "1rem",
          textTransform: "none",
          transition: "all 0.3s ease",
          lineHeight: 1.5,
          "&:hover": {
            backgroundColor: "#1976d2",
            color: "#fff",
            borderColor: "#1976d2",
          },
        }}
      >
        Aplicación instalada
      </Button>
    );
  }

  // 3. Si NO está instalada (según nuestra lógica) Y SÍ es instalable (deferredPrompt está presente),
  // mostramos el botón de "Instalar".
  if (!isPwaInstalled && isInstallable) {
    return (
      <Button
        onClick={handleButtonClick}
        variant="outlined"
        startIcon={
          <Box
            component="img"
            src="/assets/images/computer.png" // Icono de escritorio para la instalación
            alt="Icono de instalación de PWA"
            sx={{ width: 32, height: 32 }}
          />
        }
        sx={{
          borderRadius: "12px",
          borderWidth: 2,
          px: { xs: 3, md: 2 },
          py: { xs: 1.5, md: 0.5 },
          color: "#388e3c", // Color para "Instalar" (ej: verde)
          borderColor: "#388e3c",
          fontWeight: "600",
          fontSize: "1rem",
          textTransform: "none",
          transition: "all 0.3s ease",
          lineHeight: 1.5,
          "&:hover": {
            backgroundColor: "#388e3c",
            color: "#fff",
            borderColor: "#388e3c",
          },
        }}
      >
        Descargar para Escritorio
      </Button>
    );
  }

  // 4. En cualquier otro caso (móvil, o no instalable y no instalada), no renderizar nada.
  return null;
}

export default InstallPWA;
